import { Component, OnInit, Input, Optional, Self, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import * as uuid from 'uuid';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, filter, mergeMap, map, tap } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { ResizeProfilePictureModalComponent } from '../resize-profile-picture-modal/resize-profile-picture-modal.component';
import { UploadModalComponent } from '../upload-modal/upload-modal.component';

@Component({
    selector: 'app-form-profile-picture-upload',
    templateUrl: './profile-picture-upload.component.html',
    styleUrls: ['./profile-picture-upload.component.scss'],
})
export class ProfilePictureUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private destroy$ = new Subject();

    public onChange: (x?: any) => void;

    public onTouched: (x?: any) => void;

    public id: string;

    public _value: any = '';

    public t: any;

    public loading = false;

    @Input() autoUpload = false;

    @Input() disabled: boolean;

    @Input() label: string;

    @Output('change') changeOutput = new EventEmitter<any>();

    constructor(
        public dialog: MatDialog,
        @Self() @Optional() private ngControl: NgControl,
        private translateService: TranslateService,
        private userService: UserService,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.translateService
            .get('common.profilePicture')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));
    }

    get invalid(): boolean {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid(): boolean {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine(): boolean {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty(): boolean {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched(): boolean {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    public ngOnInit(): void {
        this.id = uuid.v4();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public valueChange(v): void {
        if (v !== undefined) {
            this.value = v;
        }
    }

    public writeValue(value: any): void {
        if (value !== undefined) {
            this._value = value;
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }

    openDialog(): void {
        this.dialog
            .open(UploadModalComponent, {
                data: {
                    title: this.t.modalTitle,
                    restrictionMessage: this.t.restrictions,
                    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
                    maxFileSize: 20000000,
                    autoComplete: true,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                filter((files) => !!files && files.length > 0),
                map((files) => files[0]),
                mergeMap((file) =>
                    this.dialog
                        .open(ResizeProfilePictureModalComponent, {
                            data: { image: file },
                            panelClass: 'modal-scrollable',
                        })
                        .afterClosed(),
                ),
                mergeMap((result) => {
                    if (result && !result.retake) {
                        this.loading = true;
                        return this.userService.uploadProfilePicture(result);
                    }
                    if (result && result.retake) {
                        this.openDialog();
                    }
                    return of(undefined);
                }),
                tap((url) => this.valueChange(url)),
            )
            .subscribe();
    }

    async toBase64(file: File): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (): void => resolve(reader.result);
            reader.onerror = (error): void => reject(error);
        });
    }

    imageLoaded(): void {
        this.loading = false;
    }
}
